import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';
import { useTabState } from '../../hooks/useTabState';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import SearchInput from '../../components/SearchInput';
import RoleList from './RoleList';
import ScrollToTopButton from '../../components/ScrollToTopButton';
import Button from '../../components/Button';
import LoadingSpinner from '../../components/LoadingSpinner';
import GlobalModal from '../../components/GlobalModal';
import ReInstateStaffModal from './ReinstateStaffModal';

// TYPES
import { StaffDashboardStaffMember } from '../../api/models';

export type StaffDataTypes = Record<string, StaffDashboardStaffMember[]>;
type TabStatus = 'current' | 'past';

const Staff: React.FC = () => {
	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// Get staff types from Redux store
	const staffTypes = useAppSelector((state) => state.lists.staffTypes);

	// Check if User is Organisation Admin
	const isOrganisationAdmin = useAppSelector((state) => state.user.isOrganisationAdmin);

	// Data to filter
	const [staffData, setStaffData] = React.useState<StaffDashboardStaffMember[]>([]);

	// Data used to search, it will not be changed and will always remain as its inital set value
	const [originalStaffData, setOriginalStaffData] = React.useState<StaffDashboardStaffMember[]>([]);

	// data to store current staff and past staff
	const [currentStaffData, setCurrentStaffData] = React.useState<StaffDataTypes>({});
	const [pastStaffData, setPastStaffData] = React.useState<StaffDataTypes>({});

	// data used for ordering the sections.
	const [defaultSectionOrder, setDefaultSectionOrder] = React.useState<(string | undefined)[]>([]);
	const [dynamicSectionOrder, setDynamicSectionOrder] = React.useState<(string | undefined)[]>([]);

	const [showReinstateStaffModal, setShowReinstateStaffModal] = React.useState<boolean>(false);
	const [reinstateStaffId, setReinstateStaffId] = React.useState<string>('');

	// State to control active tab and content
	const [activeTab, setActiveTab] = useTabState<TabStatus>('current', 'tabView');

	// Function to get Staff data from api and update state
	const getStaffData = async () => {
		setIsLoading(true);

		const response = await apibridge.getStaffData();

		if (response.isError) {
			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			const { result = [] } = response;

			setStaffData(result);

			setOriginalStaffData(result);
		}

		setIsLoading(false);
	};

	// filter logic
	const filterData = () => {
		if (staffTypes && staffTypes.length) {
			const defaultDataOrder = staffTypes.map((item) => item.value);

			const dynamicDataOrder: string[] = [];
			let currentStaff: StaffDataTypes = {};
			let pastStaff: StaffDataTypes = {};

			if (staffData.length > 0) {
				staffData.forEach((staff) => {
					if (staff.staffType) {
						const role = staff.staffType;

						if (!defaultDataOrder.includes(role)) {
							defaultDataOrder.push(role);
						}

						if (!dynamicDataOrder.includes(role)) {
							dynamicDataOrder.push(role);
						}

						if (staff.isCurrentStaff) {
							if (currentStaff[role]) {
								currentStaff[role].push(staff);
							} else {
								currentStaff[role] = [];
								currentStaff[role].push(staff);
							}
						} else {
							if (pastStaff[role]) {
								pastStaff[role].push(staff);
							} else {
								pastStaff[role] = [];
								pastStaff[role].push(staff);
							}
						}
					}
				});

				setCurrentStaffData(currentStaff);
				setPastStaffData(pastStaff);

				setDefaultSectionOrder(defaultDataOrder);
				setDynamicSectionOrder(dynamicDataOrder);
			}
		}
	};

	// Calling data from api and set staff data from api to staffData state
	React.useEffect(() => {
		getStaffData();
	}, []);

	// filter data once we get the data from api.
	React.useEffect(() => {
		filterData();
	}, [staffData]);

	return (
		<>
			<div className="page-general-layout staff-page">
				{!!formErrorMessage ? (
					<div className="form-validation-error">{formErrorMessage}</div>
				) : (
					<SwitchTransition mode="out-in">
						<CSSTransition
							key={`key_${isLoading}`}
							timeout={600}
							classNames="standalone-fade"
							mountOnEnter
							unmountOnExit
						>
							{isLoading ? (
								<LoadingSpinner show={isLoading} />
							) : (
								<>
									<nav className="page-header-section mbtom-30">
										<div className="header-left">
											<Button
												className={`staff-category pdl-0 ${activeTab === 'current' ? 'active' : ''}`}
												onClick={() => setActiveTab('current')}
											>
												Current
											</Button>

											{isOrganisationAdmin && (
												<Button
													className={`staff-category ${activeTab === 'past' ? 'active' : ''}`}
													onClick={() => setActiveTab('past')}
												>
													Past
												</Button>
											)}
										</div>

										<div className="header-right">
											<SearchInput
												originalStaffData={originalStaffData}
												setStaffData={setStaffData}
												searchPlaceholder="Search"
											/>
										</div>
									</nav>

									<section className="staff-content">
										{activeTab === 'current' ? (
											<div className="mbtom-30">
												{defaultSectionOrder.map((role, index) => {
													if (role && dynamicSectionOrder.includes(role)) {
														const peopleList: StaffDashboardStaffMember[] = currentStaffData[role] || [];
														return <RoleList key={index} role={role} peopleList={peopleList} isPastProfile={false} />;
													}
												})}
											</div>
										) : activeTab === 'past' ? (
											<div className="mbtom-30">
												{defaultSectionOrder.map((role, index) => {
													if (role && dynamicSectionOrder.includes(role)) {
														const peopleList: StaffDashboardStaffMember[] = pastStaffData[role] || [];

														return (
															<RoleList
																key={index}
																role={role}
																peopleList={peopleList}
																isPastProfile={true}
																setReinstateStaffId={setReinstateStaffId}
																setShowReinstateStaffModal={setShowReinstateStaffModal}
															/>
														);
													}
												})}
											</div>
										) : null}

										<ScrollToTopButton />
									</section>
								</>
							)}
						</CSSTransition>
					</SwitchTransition>
				)}
			</div>
			<GlobalModal
				isOpen={showReinstateStaffModal}
				setIsOpen={setShowReinstateStaffModal}
				titleText="Reinstate Staff &amp; Volunteers"
			>
				<ReInstateStaffModal reinstateStaffId={reinstateStaffId} closeModal={() => setShowReinstateStaffModal(false)} />
			</GlobalModal>
		</>
	);
};

export default Staff;
